import { endOfMonth, startOfMonth } from 'date-fns';

export type InventoryCategoryReportVariant = 'sales' | 'consumed' | 'purchased';

export interface InventoryCategoryItemLine {
  itemId: string;
  name: string;
  unit: string;
  quantity: number;
  amount: number;
}

export interface InventoryCategoryGroup {
  categoryId: string | null;
  categoryName: string;
  totalAmount: number;
  totalUnits: number;
  items: InventoryCategoryItemLine[];
}

export interface InventoryCategoryEntry {
  itemId: string;
  name: string;
  unit: string;
  quantity: number;
  amount: number;
  categoryId: string | null;
  categoryName: string;
}

export const UNCATEGORIZED_KEY = '__uncategorized__';

/** Groups line entries into expandable category totals. */
export function buildInventoryByCategory(
  entries: InventoryCategoryEntry[]
): InventoryCategoryGroup[] {
  const categoryMap = new Map<
    string,
    {
      categoryId: string | null;
      categoryName: string;
      totalAmount: number;
      totalUnits: number;
      items: Map<string, InventoryCategoryItemLine>;
    }
  >();

  for (const entry of entries) {
    const qty = Math.abs(Number(entry.quantity) || 0);
    if (qty <= 0) continue;

    const amount = Number(entry.amount) || 0;
    const categoryKey = entry.categoryId ?? UNCATEGORIZED_KEY;

    let group = categoryMap.get(categoryKey);
    if (!group) {
      group = {
        categoryId: entry.categoryId,
        categoryName: entry.categoryName,
        totalAmount: 0,
        totalUnits: 0,
        items: new Map(),
      };
      categoryMap.set(categoryKey, group);
    }

    group.totalAmount += amount;
    group.totalUnits += qty;

    const existing = group.items.get(entry.itemId);
    if (existing) {
      existing.quantity += qty;
      existing.amount += amount;
    } else {
      group.items.set(entry.itemId, {
        itemId: entry.itemId,
        name: entry.name,
        unit: entry.unit,
        quantity: qty,
        amount,
      });
    }
  }

  return Array.from(categoryMap.values())
    .map((group) => ({
      categoryId: group.categoryId,
      categoryName: group.categoryName,
      totalAmount: group.totalAmount,
      totalUnits: group.totalUnits,
      items: Array.from(group.items.values()).sort(
        (a, b) => b.amount - a.amount || a.name.localeCompare(b.name)
      ),
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount || a.categoryName.localeCompare(b.categoryName));
}

export function monthRangeIso(): { start: string; end: string } {
  const now = new Date();
  return {
    start: startOfMonth(now).toISOString(),
    end: endOfMonth(now).toISOString(),
  };
}
